import React, { useState } from 'react';
import { Wifi, MapPin, Facebook, Instagram, Twitter, MessageCircle, Shield } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const HotspotPreview = ({ template }) => {
  const { design, loginConfig, popup, socialMedia, locations, pricing, termsAndConditions, sslConfig } = template;
  const [activeMethod, setActiveMethod] = useState(loginConfig?.multipleMethods?.[0] || 'userpass');

  return (
    <div className="hotspot-preview-wrapper w-full overflow-hidden">
      <div 
        className="hotspot-preview bg-white rounded-lg shadow-xl overflow-hidden origin-top-left"
        data-testid="hotspot-preview"
        style={{
          transform: 'scale(1)',
          transformOrigin: 'top left',
          width: '100%',
          maxWidth: '100%'
        }}
      >
        <style>{`
          @media (max-width: 640px) {
            .hotspot-preview {
              transform: scale(0.65) !important;
              width: 154% !important;
            }
            .hotspot-preview-wrapper {
              height: auto;
              overflow: hidden;
            }
          }
          @media (min-width: 641px) and (max-width: 1024px) {
            .hotspot-preview {
              transform: scale(0.85) !important;
              width: 118% !important;
            }
          }
        `}</style>
        <div 
          className="relative min-h-[600px] p-8"
          style={{ 
            backgroundColor: design.backgroundColor,
            color: design.textColor 
          }}
        >
        {/* SSL Badge */}
        {sslConfig?.enabled && (
          <Badge 
            className="absolute top-4 right-4 bg-green-500 text-white" 
            data-testid="preview-ssl-badge"
          >
            <Shield className="w-3 h-3 mr-1" />
            Conexión Segura
          </Badge>
        )}

        {/* Logo */}
        {design.logo && (
          <div className="flex justify-center mb-6" data-testid="preview-logo">
            <img src={design.logo} alt="Logo" className="h-20 object-contain" />
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-block p-4 rounded-full mb-4" style={{ backgroundColor: design.primaryColor + '20' }}>
            <Wifi className="w-12 h-12" style={{ color: design.primaryColor }} />
          </div>
          <h1 
            className="text-3xl font-bold mb-2" 
            style={{ color: design.primaryColor }}
            data-testid="preview-welcome-title"
          >
            {popup?.enabled && popup.title ? popup.title : 'Bienvenido a nuestra WiFi'}
          </h1>
          <p className="text-sm opacity-75" data-testid="preview-welcome-message">
            {popup?.enabled && popup.message ? popup.message : 'Conéctate a internet de forma rápida y segura'}
          </p>
        </div>

        {/* Login Form */}
        <Card className="max-w-md mx-auto p-6 mb-6" data-testid="preview-login-form">
          {loginConfig?.method !== 'multiple' && (
            <h3 className="font-semibold mb-4 text-center" data-testid="preview-login-title">
              {loginConfig?.method === 'pin' && 'Ingresa tu PIN'}
              {loginConfig?.method === 'userpass' && 'Iniciar Sesión'}
              {loginConfig?.method === 'qr' && 'Escanea el código QR'}
              {loginConfig?.method === 'social' && 'Conectarse con Redes Sociales'}
              {loginConfig?.method === 'form' && 'Completa el Formulario'}
              {loginConfig?.method === 'free' && '¡WiFi Gratis!'}
            </h3>
          )}
          
          {loginConfig?.method === 'multiple' && loginConfig.multipleMethods?.length > 0 && (
            <Tabs value={activeMethod} onValueChange={setActiveMethod} className="w-full" data-testid="preview-multiple-tabs">
              <TabsList className="grid w-full mb-4" style={{ gridTemplateColumns: `repeat(${loginConfig.multipleMethods.length}, 1fr)` }}>
                {loginConfig.multipleMethods.includes('userpass') && (
                  <TabsTrigger value="userpass" data-testid="tab-userpass">Usuario</TabsTrigger>
                )}
                {loginConfig.multipleMethods.includes('pin') && (
                  <TabsTrigger value="pin" data-testid="tab-pin">PIN</TabsTrigger>
                )}
                {loginConfig.multipleMethods.includes('qr') && (
                  <TabsTrigger value="qr" data-testid="tab-qr">QR</TabsTrigger>
                )}
              </TabsList>
              
              {loginConfig.multipleMethods.includes('userpass') && (
                <TabsContent value="userpass" data-testid="content-userpass">
                  <h3 className="font-semibold mb-3 text-center">Iniciar Sesión</h3>
                  <Input placeholder="Usuario" className="mb-3" data-testid="preview-username-input" />
                  <Input type="password" placeholder="Contraseña" className="mb-3" data-testid="preview-password-input" />
                  <Button 
                    className="w-full" 
                    style={{ 
                      backgroundColor: design.primaryColor,
                      color: '#ffffff'
                    }}
                    data-testid="preview-login-btn"
                  >
                    Conectar
                  </Button>
                </TabsContent>
              )}
              
              {loginConfig.multipleMethods.includes('pin') && (
                <TabsContent value="pin" data-testid="content-pin">
                  <h3 className="font-semibold mb-3 text-center">Ingresa tu PIN</h3>
                  <Input placeholder="Ingresa tu PIN" className="mb-3" data-testid="preview-pin-input" />
                  <Button 
                    className="w-full" 
                    style={{ 
                      backgroundColor: design.primaryColor,
                      color: '#ffffff'
                    }}
                    data-testid="preview-login-btn"
                  >
                    Conectar
                  </Button>
                </TabsContent>
              )}
              
              {loginConfig.multipleMethods.includes('qr') && (
                <TabsContent value="qr" data-testid="content-qr">
                  <h3 className="font-semibold mb-3 text-center">Escanea el código QR</h3>
                  <div className="flex justify-center mb-3" data-testid="preview-qr-placeholder">
                    {loginConfig.qrImage ? (
                      <img 
                        src={loginConfig.qrImage} 
                        alt="QR Code" 
                        className="w-48 h-48 object-contain rounded-lg border-2"
                        data-testid="preview-qr-image"
                      />
                    ) : (
                      <div className="w-48 h-48 bg-gray-200 rounded-lg flex items-center justify-center">
                        <p className="text-sm text-gray-500">Código QR aquí</p>
                      </div>
                    )}
                  </div>
                  <Button 
                    className="w-full" 
                    style={{ 
                      backgroundColor: design.primaryColor,
                      color: '#ffffff'
                    }}
                    data-testid="preview-login-btn"
                  >
                    Conectar
                  </Button>
                </TabsContent>
              )}
            </Tabs>
          )}
          
          {loginConfig?.method !== 'multiple' && (
            <>
          {loginConfig?.method === 'pin' && (
            <Input placeholder="Ingresa tu PIN" className="mb-3" data-testid="preview-pin-input" />
          )}
          
          {loginConfig?.method === 'userpass' && (
            <>
              <Input placeholder="Usuario" className="mb-3" data-testid="preview-username-input" />
              <Input type="password" placeholder="Contraseña" className="mb-3" data-testid="preview-password-input" />
            </>
          )}
          
          {loginConfig?.method === 'qr' && (
            <div className="flex justify-center mb-3" data-testid="preview-qr-placeholder">
              {loginConfig.qrImage ? (
                <img 
                  src={loginConfig.qrImage} 
                  alt="QR Code" 
                  className="w-48 h-48 object-contain rounded-lg border-2"
                  data-testid="preview-qr-image"
                />
              ) : (
                <div className="w-48 h-48 bg-gray-200 rounded-lg flex items-center justify-center">
                  <p className="text-sm text-gray-500">Código QR aquí</p>
                </div>
              )}
            </div>
          )}

          {loginConfig?.method === 'social' && (
            <div className="space-y-2 mb-3" data-testid="preview-social-login">
              {loginConfig.socialProviders?.includes('facebook') && (
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="preview-facebook-btn"
                >
                  <Facebook className="w-4 h-4 mr-2" />
                  Continuar con Facebook
                </Button>
              )}
              {loginConfig.socialProviders?.includes('google') && (
                <Button 
                  className="w-full bg-white hover:bg-gray-50 text-gray-900 border"
                  data-testid="preview-google-btn"
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continuar con Google
                </Button>
              )}
              {loginConfig.socialProviders?.includes('instagram') && (
                <Button 
                  className="w-full text-white"
                  style={{ 
                    background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)'
                  }}
                  data-testid="preview-instagram-btn"
                >
                  <Instagram className="w-4 h-4 mr-2" />
                  Continuar con Instagram
                </Button>
              )}
            </div>
          )}

          {loginConfig?.method === 'form' && (
            <div className="space-y-2 mb-3" data-testid="preview-form-login">
              {loginConfig.formFields?.includes('name') && (
                <Input placeholder="Nombre completo" data-testid="preview-form-name" />
              )}
              {loginConfig.formFields?.includes('email') && (
                <Input type="email" placeholder="Email" data-testid="preview-form-email" />
              )}
              {loginConfig.formFields?.includes('phone') && (
                <Input type="tel" placeholder="Teléfono" data-testid="preview-form-phone" />
              )}
            </div>
          )}

          {loginConfig?.method === 'free' && (
            <div className="mb-3 text-center" data-testid="preview-free-access">
              <div className="text-4xl mb-2">🎉</div>
              <p className="text-sm opacity-75">
                Haz clic en conectar para acceder gratis
              </p>
            </div>
          )}
          
          <Button 
            className="w-full" 
            style={{ 
              backgroundColor: design.primaryColor,
              color: '#ffffff'
            }}
            data-testid="preview-login-btn"
          >
            {loginConfig?.method === 'free' ? 'Conectar Gratis' : 'Conectar'}
          </Button>
            </>
          )}
        </Card>

        {/* Pricing */}
        {pricing && pricing.length > 0 && (
          <div className="mb-6" data-testid="preview-pricing-section">
            <h3 className="text-xl font-semibold text-center mb-4" data-testid="preview-pricing-title">Planes Disponibles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {pricing.map((plan, index) => (
                <Card 
                  key={index} 
                  className="p-4 border-2"
                  style={{ borderColor: design.secondaryColor + '40' }}
                  data-testid={`preview-pricing-${index}`}
                >
                  <h4 className="font-semibold mb-2" style={{ color: design.primaryColor }}>{plan.name}</h4>
                  <p className="text-2xl font-bold mb-2" data-testid={`preview-pricing-price-${index}`}>{plan.price}</p>
                  <p className="text-sm opacity-75" data-testid={`preview-pricing-duration-${index}`}>Duración: {plan.duration}</p>
                  {plan.speed && <p className="text-sm opacity-75" data-testid={`preview-pricing-speed-${index}`}>Velocidad: {plan.speed}</p>}
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Locations */}
        {locations && locations.length > 0 && (
          <div className="mb-6 max-w-2xl mx-auto" data-testid="preview-locations-section">
            <h3 className="text-xl font-semibold text-center mb-4" data-testid="preview-locations-title">Nuestras Ubicaciones</h3>
            {locations.map((location, index) => (
              <Card key={index} className="p-4 mb-3" data-testid={`preview-location-${index}`}>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 mt-1" style={{ color: design.primaryColor }} />
                  <div>
                    <h4 className="font-semibold" data-testid={`preview-location-name-${index}`}>{location.name}</h4>
                    <p className="text-sm opacity-75" data-testid={`preview-location-address-${index}`}>{location.address}</p>
                    {location.phone && <p className="text-sm opacity-75" data-testid={`preview-location-phone-${index}`}>{location.phone}</p>}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Social Media */}
        {(socialMedia?.facebook || socialMedia?.instagram || socialMedia?.twitter || socialMedia?.whatsapp) && (
          <div className="text-center mb-6" data-testid="preview-social-section">
            <p className="text-sm mb-3">Síguenos en redes sociales</p>
            <div className="flex justify-center gap-3">
              {socialMedia.facebook && (
                <a href={socialMedia.facebook} target="_blank" rel="noopener noreferrer" data-testid="preview-social-facebook">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer hover:opacity-80"
                    style={{ backgroundColor: design.primaryColor }}
                  >
                    <Facebook className="w-5 h-5 text-white" />
                  </div>
                </a>
              )}
              {socialMedia.instagram && (
                <a href={socialMedia.instagram} target="_blank" rel="noopener noreferrer" data-testid="preview-social-instagram">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer hover:opacity-80"
                    style={{ backgroundColor: design.primaryColor }}
                  >
                    <Instagram className="w-5 h-5 text-white" />
                  </div>
                </a>
              )}
              {socialMedia.twitter && (
                <a href={socialMedia.twitter} target="_blank" rel="noopener noreferrer" data-testid="preview-social-twitter">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer hover:opacity-80"
                    style={{ backgroundColor: design.primaryColor }}
                  >
                    <Twitter className="w-5 h-5 text-white" />
                  </div>
                </a>
              )}
              {socialMedia.whatsapp && (
                <a href={`https://wa.me/${socialMedia.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="preview-social-whatsapp">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer hover:opacity-80"
                    style={{ backgroundColor: design.primaryColor }}
                  >
                    <MessageCircle className="w-5 h-5 text-white" />
                  </div>
                </a>
              )}
            </div>
          </div>
        )}

        {/* Terms */}
        {termsAndConditions && (
          <div className="text-center max-w-2xl mx-auto" data-testid="preview-terms">
            <p className="text-xs opacity-60">{termsAndConditions}</p>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default HotspotPreview;
